import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { ButtonComponent } from '../ui-button/button.component';
import { ConnectionStateService } from '../../core/services/connection-state.service';

/**
 * Глобальный оверлей обрыва связи (зеркало мини-аппа).
 *
 * Виден, когда ConnectionStateService.offline() === true (выставляет
 * network-resilience-интерсептор после исчерпания ретраев на сетевой ошибке).
 * «Повторить» — перевыпустить последний упавший запрос (см.
 * ConnectionStateService.retry()): на успехе оверлей снимется, на ошибке
 * останется; «Перезагрузить» — window.location.reload(). Стилистика повторяет
 * DataStateComponent (карточка на --bg-card / --border-light / --radius-lg).
 */
@Component({
  selector: 'app-connection-error',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ButtonComponent],
  template: `
    @if (connectionState.offline()) {
      <div class="conn-overlay" role="alertdialog" aria-modal="true" aria-labelledby="conn-title">
        <div class="conn-card">
          <h2 id="conn-title" class="conn-title">Не удалось связаться с сервером</h2>
          <p class="conn-message">Проверьте интернет-соединение и повторите попытку.</p>
          <div class="conn-actions">
            <app-button variant="primary" size="lg" (clicked)="onRetry()">
              Повторить
            </app-button>
            <button type="button" class="conn-reload" (click)="onReload()">
              Перезагрузить
            </button>
          </div>
        </div>
      </div>
    }
  `,
  styles: [`
    .conn-overlay {
      position: fixed;
      inset: 0;
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 1.5rem;
      background: rgba(9, 9, 9, 0.8);
      backdrop-filter: blur(4px);
    }

    .conn-card {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      width: 100%;
      max-width: 420px;
      padding: 2.5rem 2rem;
      background: var(--bg-card);
      border: 1px solid var(--border-light);
      border-radius: var(--radius-lg);
    }

    .conn-title {
      color: var(--text-primary);
      font-size: 1.5rem;
      font-weight: 600;
      margin: 0 0 0.5rem 0;
    }

    .conn-message {
      color: var(--text-secondary);
      font-size: 1rem;
      margin: 0 0 1.5rem 0;
    }

    .conn-actions {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 0.75rem;
      width: 100%;
    }

    .conn-reload {
      background: none;
      border: none;
      padding: 0.25rem 0.5rem;
      color: var(--text-secondary);
      font-family: var(--font-main);
      font-size: 0.875rem;
      font-weight: 600;
      text-decoration: underline;
      text-underline-offset: 4px;
      cursor: pointer;
    }

    .conn-reload:hover {
      color: var(--text-primary);
    }
  `]
})
export class ConnectionErrorComponent {
  protected readonly connectionState = inject(ConnectionStateService);

  protected onRetry(): void {
    this.connectionState.retry();
  }

  protected onReload(): void {
    window.location.reload();
  }
}
